import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AuditActivity, AuditTrailDto } from 'src/app/models/auditTrail.model';
import { Role } from 'src/app/models/role.model';
import { AuditTrailService } from 'src/app/services/audit-trail.service';
import { RoleService } from 'src/app/services/role.service';
import { PermissionsService } from 'shared-lib';

@Component({
  selector: 'app-audit-trail',
  templateUrl: './audit-trail.component.html',
  styleUrls: ['./audit-trail.component.scss'],
})
export class AuditTrailComponent implements OnInit {
  auditTrails: AuditTrailDto[] = [];
  selectedUtilities: number;
  showSelect: boolean = true;

  dateTo: string = new Date().toISOString().slice(0, 10);
  startDate = new Date();
  temp = this.startDate.setDate(this.startDate.getDate() - 7);
  dateFrom: string = this.startDate.toISOString().slice(0, 10);

  count: number = 0;
  pageSize: number = 10;
  currentPage: number = 1;

  searchText: string = '';
  selectedActivity: AuditActivity | null = null;
  selectedRoleId: number | null = null;

  roles: Role[] = [];

  activityOptions = [
    { label: 'AUDIT_TRAIL.ACTIVITY_TYPE.CREATION', value: AuditActivity.Creation },
    { label: 'AUDIT_TRAIL.ACTIVITY_TYPE.MODIFICATION', value: AuditActivity.Modification },
    { label: 'AUDIT_TRAIL.ACTIVITY_TYPE.DELETION', value: AuditActivity.Deletion },
    { label: 'AUDIT_TRAIL.ACTIVITY_TYPE.DOWNLOAD', value: AuditActivity.Download },
    { label: 'AUDIT_TRAIL.ACTIVITY_TYPE.UPLOAD', value: AuditActivity.Upload },
  ];

  isJsonModalOpened: boolean = false;
  selectedJsonLines: string[] = [];
  jsonCopied: boolean = false;

  constructor(
    private auditTrailService: AuditTrailService,
    private roleService: RoleService,
    private toastr: ToastrService,
    public permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.getRoles();
    this.checkUtilityId();
  }

  checkUtilityId() {
    var x = localStorage.getItem('utilityId');
    if (x) {
      var utility = +x;
      if (utility != 0) {
        this.showSelect = false;
        this.selectedUtilities = utility;
        this.reloadTable(1);
      }
    }
  }

  getRoles() {
    this.roleService.getAll(false).subscribe({
      next: (response) => {
        this.roles = response.body?.data ?? [];
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  selectUtilities(event: any) {
    this.selectedUtilities = event;
    this.reloadTable(1);
  }

  modulePage(trail: AuditTrailDto): string {
    return [trail.module, trail.page].filter((v) => !!v).join(' / ');
  }

  activityLabel(activity: AuditActivity | null | undefined): string {
    const option = this.activityOptions.find((o) => o.value === activity);
    return option ? option.label : '';
  }

  selectActivity(e: any) {
    this.selectedActivity = e.target.value === '' ? null : (e.target.value as AuditActivity);
    this.reloadTable(1);
  }

  selectRole(e: any) {
    this.selectedRoleId = e.target.value === '' ? null : Number(e.target.value);
    this.reloadTable(1);
  }

  private getAll(obj: any) {
    this.auditTrailService.getAll(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.auditTrails = responseData.data.data;
          this.count = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  reloadTable(page: any = null) {
    if (page) this.currentPage = page;

    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: this.getFilterParams(),
    };

    this.getAll(obj);
  }

  getFilterParams() {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      search: this.searchText ? this.searchText.trim() : null,
      activity: this.selectedActivity,
      roleId: this.selectedRoleId,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      utilityId: this.selectedUtilities,
      timezoneValue: timeZone,
    };
  }

  search() {
    this.reloadTable(1);
  }

  onSearchInput() {
    if (!this.searchText) {
      this.reloadTable(1);
    }
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  download() {
    let obj = {
      pageInfo: {
        page: -1,
        pageSize: this.pageSize,
      },
      filterParams: this.getFilterParams(),
    };

    this.auditTrailService.download(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else {
          this.toastr.error(data?.message || 'Error occured');
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  openJson(trail: AuditTrailDto) {
    this.auditTrailService.getValueChanges(trail.id, this.selectedUtilities).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          const formatted = this.formatJson(responseData.data);
          this.selectedJsonLines = formatted ? formatted.split('\n') : [];
          this.isJsonModalOpened = true;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  private formatJson(json: string): string {
    if (!json) return '';
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  }

  copyJson() {
    const text = this.selectedJsonLines.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      this.jsonCopied = true;
      setTimeout(() => (this.jsonCopied = false), 2000);
    });
  }

  closeJson() {
    this.isJsonModalOpened = false;
    this.selectedJsonLines = [];
    this.jsonCopied = false;
  }
}
